import { apiClient } from '../../lib/apiClient';
import type {
  FacebookExchangeCreate,
  FacebookExchangeRead,
  FacebookSelectCreate,
  LinkedInAuthorizeRead,
  LinkedInExchangeCreate,
  PlatformConnectionRead,
} from '../../types/api';

/**
 * Connected accounts: the platforms this business can publish to, and the OAuth
 * dance that connects one.
 *
 * Business-scoped like every owned resource (SRS §4). Nothing here ever handles
 * a token — the authorize call hands back a URL to leave for, and the exchange
 * call trades LinkedIn's `code` for a connection the server stores encrypted.
 * The read model carries a display name and an expiry and nothing else.
 */
function connectionsPath(businessId: string): string {
  return `/api/v1/businesses/${businessId}/platform-connections`;
}

/**
 * Every platform, connected or not — one row per {@link PlatformName}, so the
 * UI draws the same cards whatever the account has done. A platform this
 * deployment has no app for comes back `available: false`.
 */
export function listConnections(
  businessId: string,
  signal?: AbortSignal,
): Promise<PlatformConnectionRead[]> {
  return apiClient.get<PlatformConnectionRead[]>(connectionsPath(businessId), { signal });
}

/**
 * Starts the connect flow. Owner only.
 *
 * Returns the LinkedIn authorization URL, already carrying a short-lived signed
 * `state` that binds this business and this user — so the caller stores nothing
 * locally before navigating away, and a callback replayed against another
 * account is refused. Domain failures: 403 for a member, 503
 * `INTEGRATION_DISABLED` when the server has no LinkedIn app configured.
 */
export function authorizeLinkedIn(businessId: string): Promise<LinkedInAuthorizeRead> {
  return apiClient.post<LinkedInAuthorizeRead>(`${connectionsPath(businessId)}/linkedin/authorize`);
}

/**
 * Finishes the connect flow with the `code` and `state` LinkedIn put on the
 * callback URL, and returns the stored connection.
 *
 * Sent through the normal authenticated client, which is the whole reason the
 * redirect lands on the SPA rather than the API: the `code` never touches an
 * unauthenticated route. Domain failures: `INVALID_OAUTH_STATE` (400),
 * `INTEGRATION_DISABLED` (503), `LINKEDIN_UNAVAILABLE`.
 */
export function exchangeLinkedIn(
  businessId: string,
  body: LinkedInExchangeCreate,
): Promise<PlatformConnectionRead> {
  return apiClient.post<PlatformConnectionRead>(
    `${connectionsPath(businessId)}/linkedin/exchange`,
    {
      body,
    },
  );
}

/**
 * Forgets the connection and its stored token. Owner only.
 *
 * LinkedIn publishes no revoke endpoint for member tokens, so this is our side
 * of the link only: the token stops being usable by us, and stays valid at
 * LinkedIn until it expires on its own.
 */
export function disconnectLinkedIn(businessId: string): Promise<void> {
  return apiClient.delete<void>(`${connectionsPath(businessId)}/linkedin`);
}

/**
 * Starts the Facebook Page connect flow (Facebook Login for Business). Owner
 * only. Same contract as {@link authorizeLinkedIn}: a URL carrying a signed
 * `state`, 403 for a member, 503 `INTEGRATION_DISABLED` without a Meta app.
 */
export function authorizeFacebook(businessId: string): Promise<LinkedInAuthorizeRead> {
  return apiClient.post<LinkedInAuthorizeRead>(`${connectionsPath(businessId)}/facebook/authorize`);
}

/**
 * Trades Meta's `code` for either a connected Page (one shared) or the list of
 * Pages to choose from plus a `selection_ticket`. Domain failures:
 * `INVALID_OAUTH_STATE`, `FACEBOOK_NO_PAGES`, `INTEGRATION_DISABLED`,
 * `META_UNAVAILABLE`, `META_REJECTED`.
 */
export function exchangeFacebook(
  businessId: string,
  body: FacebookExchangeCreate,
): Promise<FacebookExchangeRead> {
  return apiClient.post<FacebookExchangeRead>(`${connectionsPath(businessId)}/facebook/exchange`, {
    body,
  });
}

/**
 * Connects the Page the user picked from the exchange's list. The server takes
 * the Page token from Meta again, never from us. `FACEBOOK_SELECTION_EXPIRED`
 * when the ticket is older than ten minutes or not this caller's.
 */
export function selectFacebookPage(
  businessId: string,
  body: FacebookSelectCreate,
): Promise<PlatformConnectionRead> {
  return apiClient.post<PlatformConnectionRead>(`${connectionsPath(businessId)}/facebook/select`, {
    body,
  });
}

/** Forgets the Facebook connection and its stored Page token. Owner only, idempotent. */
export function disconnectFacebook(businessId: string): Promise<void> {
  return apiClient.delete<void>(`${connectionsPath(businessId)}/facebook`);
}
